import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation } from '@fortawesome/pro-light-svg-icons/faCircleExclamation';
import { faTowerBroadcast } from '@fortawesome/pro-light-svg-icons/faTowerBroadcast';
import { TypedMemo } from '../../common/types';
import { SerializedScheduledPost, SerializedSocialChannel } from '../promote/types';
import { platformMeta } from '../promote/platformMeta';
import { artistHomeMessages as messages } from './artistHomeMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Home; each part uses its own slice of it */
import styles from './ArtistHome.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PromoteNeedsYouProps {
  failed: SerializedScheduledPost[];
  reconnect: SerializedSocialChannel[];
  onReview: () => void;
  onReconnect: () => void;
}

/** Failed posts ("Review") and channels that need a new sign-in ("Reconnect") (design a1). */
const PromoteNeedsYouComponent: React.FC<PromoteNeedsYouProps> = ({ failed, reconnect, onReview, onReconnect }) => {
  const intl = useIntl();
  if (failed.length === 0 && reconnect.length === 0) {
    return null;
  }

  return (
    <div className={clsx(styles.promoColumn, styles.promoNeedsYou)}>
      <div className={styles.sectionKey}>{intl.formatMessage(messages.promoteNeedsYou)}</div>
      {failed.length > 0 && (
        <div className={clsx(styles.alert, styles.alertError)}>
          <FontAwesomeIcon icon={faCircleExclamation} />
          <div className={styles.grow}>
            <b>{intl.formatMessage(messages.promoteFailedPosts, { count: failed.length })}</b>
            {failed[0]?.failReason !== null && failed[0]?.failReason !== undefined && (
              <small>{failed[0].failReason}</small>
            )}
          </div>
          <button type="button" className={styles.linkButton} onClick={onReview}>
            {intl.formatMessage(messages.promoteReview)}
          </button>
        </div>
      )}
      {reconnect.map((channel) => (
        <div key={channel.platform} className={clsx(styles.alert, styles.alertInfo)}>
          <FontAwesomeIcon icon={faTowerBroadcast} />
          <div className={styles.grow}>
            <b>
              {intl.formatMessage(messages.promoteReconnectTitle, { channel: platformMeta[channel.platform].name })}
            </b>
            <small>{intl.formatMessage(messages.promoteReconnectBody)}</small>
          </div>
          <button type="button" className={styles.linkButton} onClick={onReconnect}>
            {intl.formatMessage(messages.promoteReconnect)}
          </button>
        </div>
      ))}
    </div>
  );
};

export const PromoteNeedsYou = TypedMemo(PromoteNeedsYouComponent);
